import type { ReactNode } from 'react';
import { Icon, type IconName } from '../../primitives/icon/index.ts';
import { Text } from '../../primitives/Text.tsx';
import styles from './StatTile.module.css';

/**
 * Design system § 14 — Stat tile, and the row it comes in.
 *
 * The smallest surface that carries a figure: a label, a value, and at most
 * one supporting line. Not a card — § 7 says an isolated metric sits as free
 * text on the background rather than in a box, and what earns the box here is
 * that these come in a ROW and are read across.
 *
 * EVERY VALUE IS A REAL COUNT. A tile whose number the platform does not have
 * takes `value={null}` and shows an em dash with the reason underneath, rather
 * than a placeholder figure that makes an empty dashboard look populated. That
 * is a rule about honesty, not about layout: a fabricated number on an
 * administrative screen is a decision someone will take.
 */

interface StatTileProps {
  label: string;
  /** The real count. `null` renders an em dash — never a stand-in number. */
  value: number | string | null;
  /** One line under the figure: what it counts, or why it is not known yet. */
  hint?: string;
  icon?: IconName;
}

export function StatTile({ label, value, hint, icon }: StatTileProps) {
  return (
    <div className={styles.tile}>
      <div className={styles.head}>
        <Text as="span" variant="label" tone="muted">
          {label}
        </Text>
        {icon ? (
          <span className={styles.icon} aria-hidden="true">
            <Icon name={icon} size={16} />
          </span>
        ) : null}
      </div>

      <Text as="p" variant="token-lg">
        {value === null ? '—' : value}
      </Text>

      {hint ? (
        <Text as="p" variant="body-sm" tone="muted">
          {hint}
        </Text>
      ) : null}
    </div>
  );
}

/**
 * The row the tiles come in — a grid that wraps, four across where there is
 * room. § 14: there is never one tile alone, because a single figure with no
 * comparison is a sentence and belongs in text.
 */
export function StatRow({ children, label }: { children: ReactNode; label: string }) {
  return (
    <section className={styles.row} aria-label={label}>
      {children}
    </section>
  );
}
